import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import { Delivery } from 'src/app/models';
import { BookingItem } from 'src/app/models/booking-item';
import { DeliveryType } from 'src/app/models/deliveries/delivery-type';
import { AssignItemsDialogComponent } from 'src/app/shared/dialogs/assign-items-dialog/assign-items-dialog.component';
import { CompleteDeliveryDialogComponent } from 'src/app/shared/dialogs/complete-delivery-dialog/complete-delivery-dialog.component';
import { TrackedAttributesPromptComponent } from 'src/app/shared/item-attributes/tracked-attributes-prompt/tracked-attributes-prompt.component';
import { DeliveryService } from 'src/app/shared/services/delivery.service';
import { SignatureCaptureDialogComponent } from 'src/app/shared/signature-capture/signature-capture-dialog/signature-capture-dialog.component';
import { AlertTextComponent } from '../../../../../shared/alert-text/alert-text.component';

@Component({
  selector: 'app-delivery-items-list',
  standalone: true,
  templateUrl: './delivery-items-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertTextComponent,
    MatButton,
    NgxSkeletonLoaderModule,
    TrackedAttributesPromptComponent
  ]
})
export class DeliveryItemsListComponent implements OnDestroy {
  canUpdateItems = computed(
    () =>
      this.delivery().fulfilled_date === null &&
      this.delivery().deliverable_type === DeliveryType.BOOKING &&
      this.delivery().type === 'delivery'
  );
  deliveryService = inject(DeliveryService);
  delivery = input.required<Delivery>();
  dialog = inject(MatDialog);
  completed = output<void>();
  itemIdsAttributePending = signal([]);

  destroy = new Subject<void>();

  deliveryItems = injectQuery(() => ({
    enabled: Boolean(this.delivery().id),
    queryKey: ['delivery', this.delivery().id, 'items'],
    queryFn: () =>
      lastValueFrom(this.deliveryService.getDeliveryItems(this.delivery().id))
  }));

  needsItemsAssigned = computed(
    () =>
      this.deliveryItems
        .data()
        ?.deliveryItems?.some((item) => item.needsAssigned) ?? false
  );

  openAssignItemsDialog() {
    const items = this.deliveryItems
      .data()
      ?.deliveryItems.map(({ sourceItem }) => sourceItem);

    const booking = this.delivery().deliverable;
    const itemsIncludingChildItems = this.deliveryItems
      .data()
      .deliveryItems.map((item) => item.sourceItem as BookingItem)
      .flatMap((deliverableItem) => {
        if (deliverableItem.children.length) {
          return Array.from(
            { length: deliverableItem.quantity },
            () => deliverableItem
          ).flatMap(({ children }) => children);
        }
        return [deliverableItem];
      });

    this.dialog
      .open(AssignItemsDialogComponent, {
        data: {
          booking: {
            ...booking,
            items: itemsIncludingChildItems
          }
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.deliveryItems.refetch();
      });
  }

  completeDelivery() {
    const deliveryNoteLines = this.deliveryItems
      .data()
      ?.deliveryItems.map(({ name }) => name);
    this.dialog
      .open(CompleteDeliveryDialogComponent, {
        data: {
          delivery: this.delivery(),
          deliveryNoteLines
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.completed.emit();
        this.deliveryItems.refetch().then(() => {
          this.itemIdsAttributePending.set(
            this.deliveryItems.data().deliveryItems.map(({ id }) => id)
          );
        });
      });
  }

  openSignatureCaptureDialog() {
    this.dialog.open(SignatureCaptureDialogComponent, {
      data: {
        id: this.delivery().deliverable_id,
        type: this.delivery().deliverable_type,
        deliveryId: this.delivery().id,
        stage: this.delivery().type === 'delivery' ? 'Delivery' : 'Collection'
      }
    });
  }

  attributesUpdated(id: number) {
    this.itemIdsAttributePending.set(
      this.itemIdsAttributePending().filter((i) => i !== id)
    );
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }
}
